import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { submissionAPI } from '../services/api';
import { FormattedText } from '../utils/formatText';
import { useToast } from '../components/Toast';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeDragDropResults from '../components/CodeDragDropResults';

function QuizAttempts() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [flaggingQuestion, setFlaggingQuestion] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);

  useEffect(() => {
    fetchQuizSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const fetchQuizSubmissions = async () => {
    try {
      setLoading(true);
      const response = await submissionAPI.getMyAttempts(quizId);
      
      if (!response.data.attempts || response.data.attempts.length === 0) {
        showToast('No attempts found for this quiz', 'error');
        navigate('/my-quizzes');
        return;
      }
      
      setSubmissions(response.data.attempts);
      
      // Load flags for all submissions
      loadAllFlags(response.data.attempts);
    } catch (error) {
      console.error('Error fetching attempts:', error);
      showToast('Failed to load quiz attempts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAllFlags = async (allSubmissions) => {
    try {
      const flagsMap = {};
      await Promise.all(allSubmissions.map(async (sub) => {
        const response = await submissionAPI.getSubmissionFlags(sub._id);
        response.data.forEach(flag => {
          const key = `${sub._id}_${flag.question.toString()}`;
          flagsMap[key] = flag;
        });
      }));
      
      setFlaggedQuestions(flagsMap);
    } catch (error) {
      console.error('Error loading flags:', error);
    }
  };

  const handleFlagQuestion = async (submissionId, questionId) => {
    if (!flagReason.trim()) {
      showToast('Please provide a reason for flagging this question', 'error');
      return;
    }

    try {
      await submissionAPI.flagQuestion({
        submissionId: submissionId,
        questionId: questionId,
        reason: flagReason
      });
      
      const key = `${submissionId}_${questionId}`;
      setFlaggedQuestions({
        ...flaggedQuestions,
        [key]: { status: 'pending', reason: flagReason }
      });
      
      setFlaggingQuestion(null);
      setFlagReason('');
      showToast('Question flagged successfully. Admin will review it.', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to flag question', 'error');
    }
  };

  if (loading) {
    return <div className="loading">Loading quiz attempts...</div>;
  }

  if (submissions.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <p>No attempts found for this quiz.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/my-quizzes')}>
            ← Back to My Quizzes
          </button>
        </div>
      </div>
    );
  }

  const mainSubmission = submissions[0];
  const quiz = mainSubmission.quiz;

  return (
    <div className="container">
      <div className="card">
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/my-quizzes')}
          style={{ fontSize: '12px', marginBottom: '16px' }}
        >
          ← Back to My Quizzes
        </button>

        <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
          {quiz.title}
        </h2>
        {quiz.description && (
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            {quiz.description}
          </p>
        )}

        <div style={{ 
          padding: '12px 16px',
          backgroundColor: '#f0f4ff',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '13px',
          color: '#4a5568'
        }}>
          <strong>Total Attempts:</strong> {submissions.length} ({submissions.filter(s => s.attemptNumber > 1).length} practice)
        </div>

        {/* Tabs for Attempts */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          flexWrap: 'wrap'
        }}>
          {submissions.map((submission, idx) => {
            const isActive = selectedAttemptIndex === idx;
            const percentage = ((submission.score / submission.totalQuestions) * 100).toFixed(2);
            const passed = percentage >= 50;
            
            return (
              <button
                key={submission._id}
                onClick={() => setSelectedAttemptIndex(idx)}
                style={{
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isActive 
                    ? (submission.attemptNumber === 1 ? '#4c51bf' : '#92400e')
                    : '#718096',
                  background: isActive 
                    ? (submission.attemptNumber === 1 ? '#eef2ff' : '#fef3c7')
                    : 'transparent',
                  border: 'none',
                  borderBottom: isActive 
                    ? `3px solid ${submission.attemptNumber === 1 ? '#667eea' : '#f59e0b'}`
                    : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  borderRadius: '8px 8px 0 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = '#f7fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span>
                  {submission.attemptNumber === 1 
                    ? '⭐ Official' 
                    : `🔄 Practice #${submission.attemptNumber - 1}`}
                </span>
                {quiz.showResultsToStudents !== false && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: passed ? '#2f855a' : '#c53030'
                  }}>
                    {percentage}%
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Attempt Details */}
        {(() => {
          const submission = submissions[selectedAttemptIndex];
          const percentage = ((submission.score / submission.totalQuestions) * 100).toFixed(2);
          const passed = percentage >= 50;

          return (
            <div>
              <div style={{
                padding: '16px 20px',
                backgroundColor: submission.attemptNumber === 1 ? '#eef2ff' : '#fef3c7',
                borderRadius: '12px',
                marginBottom: '24px',
                border: `2px solid ${submission.attemptNumber === 1 ? '#667eea' : '#f59e0b'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <h4 style={{ 
                    color: submission.attemptNumber === 1 ? '#4c51bf' : '#92400e', 
                    fontSize: '18px', 
                    fontWeight: '700',
                    margin: 0
                  }}>
                    {submission.attemptNumber === 1 ? '⭐ Official Attempt' : `🔄 Practice Attempt #${submission.attemptNumber - 1}`}
                  </h4>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {quiz.showResultsToStudents !== false && (
                      <div style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        backgroundColor: passed ? '#c6f6d5' : '#fed7d7',
                        textAlign: 'center'
                      }}>
                        <span style={{
                          fontSize: '20px',
                          fontWeight: '700',
                          color: passed ? '#2f855a' : '#c53030'
                        }}>
                          {percentage}%
                        </span>
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#4a5568', fontWeight: '500' }}>
                      📅 {new Date(submission.submittedAt).toLocaleDateString()}<br/>
                      🕐 {new Date(submission.submittedAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Results */}
              {submission.detailedResults && submission.detailedResults.length > 0 && (
                <div style={{ paddingLeft: '8px' }}>
                  {submission.detailedResults.map((item, index) => {
                    if (!item || !item.question) return null;
                    
                    const question = item.question;
                    const questionType = item.questionType || 'MCQ';
                    const isFlagged = flaggedQuestions[`${submission._id}_${item.questionId}`];
                    
                    return (
                      <div 
                        key={item.questionId} 
                        style={{ 
                          marginBottom: '20px',
                          padding: '16px',
                          borderRadius: '8px',
                          backgroundColor: item.isCorrect ? '#f0fdf4' : '#fef2f2',
                          border: `1px solid ${item.isCorrect ? '#bbf7d0' : '#fecaca'}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ 
                              fontSize: '11px', 
                              fontWeight: '700', 
                              color: item.isCorrect ? '#15803d' : '#b91c1c',
                              textTransform: 'uppercase'
                            }}>
                              Question {index + 1} {item.isCorrect ? '✓ CORRECT' : '✗ INCORRECT'}
                            </span>
                          </div>
                          {!isFlagged && (
                            <button
                              onClick={() => setFlaggingQuestion(`${submission._id}_${item.questionId}`)}
                              style={{
                                padding: '4px 12px',
                                fontSize: '11px',
                                backgroundColor: 'transparent',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#4a5568'
                              }}
                            >
                              🚩 Flag Issue
                            </button>
                          )}
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <FormattedText text={question} style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c' }} />
                        </div>

                        {/* Code Reference */}
                        {item.codeReference && (
                          <div style={{ marginBottom: '12px' }}>
                            <SyntaxHighlighter
                              language={item.codeLanguage || 'javascript'}
                              style={vscDarkPlus}
                              customStyle={{
                                borderRadius: '6px',
                                fontSize: '12px',
                                padding: '12px'
                              }}
                            >
                              {item.codeReference}
                            </SyntaxHighlighter>
                          </div>
                        )}

                        {/* MCQ Options */}
                        {questionType === 'MCQ' && item.options && Array.isArray(item.options) && (
                          <>
                            {item.options.map((option, optIndex) => (
                              <div 
                                key={optIndex}
                                style={{ 
                                  margin: '4px 0',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  backgroundColor: 
                                    optIndex === item.correctAnswer ? '#c6f6d5' :
                                    optIndex === item.selectedAnswer && !item.isCorrect ? '#fed7d7' :
                                    '#f7fafc',
                                  border: optIndex === item.correctAnswer || (optIndex === item.selectedAnswer && !item.isCorrect) ? 
                                    `1.5px solid ${optIndex === item.correctAnswer ? '#48bb78' : '#f56565'}` : '1px solid #e2e8f0',
                                  color: 
                                    optIndex === item.correctAnswer ? '#2f855a' :
                                    optIndex === item.selectedAnswer && !item.isCorrect ? '#c53030' :
                                    '#4a5568',
                                  fontWeight: 
                                    optIndex === item.correctAnswer || optIndex === item.selectedAnswer ? 
                                    '600' : '500'
                                }}
                              >
                                <span style={{ fontWeight: '700', marginRight: '6px' }}>
                                  {String.fromCharCode(65 + optIndex)}.
                                </span>
                                <FormattedText text={option} />
                                {optIndex === item.correctAnswer && <span style={{ marginLeft: '6px', fontSize: '12px' }}>✓ Correct</span>}
                                {optIndex === item.selectedAnswer && !item.isCorrect && <span style={{ marginLeft: '6px', fontSize: '11px' }}>(Your answer)</span>}
                              </div>
                            ))}
                          </>
                        )}

                        {/* True/False */}
                        {questionType === 'TrueFalse' && (
                          <div>
                            {[true, false].map((option, optIndex) => {
                              const isCorrectOption = item.correctAnswer === optIndex;
                              const isSelectedOption = item.selectedAnswer === optIndex;
                              
                              return (
                                <div 
                                  key={optIndex}
                                  style={{
                                    margin: '4px 0',
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: 
                                      isCorrectOption ? '#c6f6d5' :
                                      isSelectedOption && !item.isCorrect ? '#fed7d7' :
                                      '#f7fafc',
                                    border: isCorrectOption || (isSelectedOption && !item.isCorrect) ? 
                                      `1.5px solid ${isCorrectOption ? '#48bb78' : '#f56565'}` : '1px solid #e2e8f0',
                                    color: 
                                      isCorrectOption ? '#2f855a' :
                                      isSelectedOption && !item.isCorrect ? '#c53030' :
                                      '#4a5568',
                                    fontWeight: isCorrectOption || isSelectedOption ? '600' : '500'
                                  }}
                                >
                                  {option ? 'True' : 'False'}
                                  {isCorrectOption && <span style={{ marginLeft: '6px', fontSize: '12px' }}>✓ Correct</span>}
                                  {isSelectedOption && !item.isCorrect && <span style={{ marginLeft: '6px', fontSize: '11px' }}>(Your answer)</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Open Ended */}
                        {questionType === 'OpenEnded' && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{
                              padding: '10px 12px',
                              background: '#f7fafc',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <strong style={{ fontSize: '11px', color: '#4a5568', textTransform: 'uppercase' }}>
                                Your Answer:
                              </strong>
                              <p style={{ margin: '6px 0 0 0', color: '#2d3748', fontSize: '12px' }}>
                                {item.selectedAnswer || 'No answer provided'}
                              </p>
                            </div>
                            {item.correctAnswer && (
                              <div style={{
                                padding: '10px 12px',
                                background: '#c6f6d5',
                                borderRadius: '6px',
                                marginTop: '8px',
                                border: '1px solid #48bb78'
                              }}>
                                <strong style={{ fontSize: '11px', color: '#2f855a', textTransform: 'uppercase' }}>
                                  Correct Answer:
                                </strong>
                                <p style={{ margin: '6px 0 0 0', color: '#2f855a', fontSize: '12px' }}>
                                  {item.correctAnswer}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Code Drag & Drop */}
                        {questionType === 'CodeDragDrop' && item.selectedAnswer && (
                          <CodeDragDropResults
                            correctCode={item.correctAnswer}
                            userAnswer={item.selectedAnswer}
                            isCorrect={item.isCorrect}
                          />
                        )}

                        {/* Explanation */}
                        {!item.isCorrect && item.explanation && (
                          <div style={{
                            marginTop: '10px',
                            padding: '10px 12px',
                            background: '#edf2f7',
                            borderLeft: '3px solid #4299e1',
                            borderRadius: '6px'
                          }}>
                            <strong style={{ fontSize: '11px', color: '#2c5282', textTransform: 'uppercase' }}>
                              💡 Explanation:
                            </strong>
                            <FormattedText text={item.explanation} style={{ marginTop: '6px', color: '#2d3748', fontSize: '12px' }} />
                          </div>
                        )}

                        {/* Flag Modal */}
                        {flaggingQuestion === `${submission._id}_${item.questionId}` && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#fff7ed',
                            border: '1.5px solid #fb923c',
                            borderRadius: '8px'
                          }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#c2410c' }}>
                              Flag This Question
                            </h5>
                            <textarea
                              value={flagReason}
                              onChange={(e) => setFlagReason(e.target.value)}
                              placeholder="Describe the issue with this question..."
                              rows="3"
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #fdba74',
                                fontSize: '12px',
                                marginBottom: '8px'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleFlagQuestion(submission._id, item.questionId)}
                                className="btn btn-primary"
                                style={{ fontSize: '11px', padding: '6px 12px' }}
                              >
                                Submit Flag
                              </button>
                              <button
                                onClick={() => {
                                  setFlaggingQuestion(null);
                                  setFlagReason('');
                                }}
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '6px 12px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default QuizAttempts;
